import { Alert, Box, Button, CircularProgress, Popover, TextField, Typography } from '@mui/material';
import { JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPercent } from '../../../library/dashboardDisplay';
import { formatRecordDate } from '../../../library/dateTimeHelper';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { CatalogTypeChoice, catalogTypeChoices, IServiceCatalog } from '../../../library/models/serviceCatalog';
import {
    ITherapistRevenueShare,
    shareEditValue,
    shareFor,
} from '../../../library/models/therapistRevenueShare';
import { useApiFormError } from '../../../library/useApiFormError';
import {
    REVENUE_SHARE_PERCENT_BOUNDS,
    numericSlotProps,
} from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { createTherapistShareSchema, TherapistShareFormValues } from './therapistShareSchema';
import {
    actionsRowStyles,
    blockedSaveStyles,
    confirmActionsStyles,
    disciplineRowStyles,
    disciplinesHintStyles,
    disciplinesTitleStyles,
    explanationStyles,
    fieldRowStyles,
    fieldStyles,
    formAlertStyles,
    loadingStyles,
    popoverPaperStyles,
    popoverTitleStyles,
    suffixStyles,
} from './therapistShare.styles';

interface TherapistSharePopoverProps {
    /** The element the popover hangs off. Never null: the caller mounts this only when open. */
    anchor: HTMLElement;
    therapistId: number;
    therapistName: string;
    /** Every scope this therapist has a rate in — the default and any overrides. */
    shares: ITherapistRevenueShare[];
    onClose: () => void;
    /** Awaited on success, so the table is refreshed before the popover closes. */
    onSaved: () => Promise<unknown>;
}

/**
 * Setting what a therapist keeps, in a popover hung off the value itself.
 *
 * <b>A popover rather than a dialog, and rather than the table cell.</b> A dialog is ceremony for
 * a few numbers and blocks the page it is about; a cell is too small for a number that also needs
 * a sentence explaining when it starts and a way to remove it. A popover is the weight in between
 * — anchored to what it edits, dismissible, and roomy enough that nothing has to be abbreviated
 * into a column.
 *
 * <b>A default, then one optional field per discipline</b> — "40%, but 50% of massage". The
 * disciplines listed are the ones the therapist practises, plus any they still have an override
 * for, so a rate is never invisible and uneditable. Blank uses the default.
 *
 * The form is mounted only once both lists have arrived, because it seeds itself once from its
 * defaults (see `TherapistShareForm`): a discipline list that arrived after the seed would have
 * no values to open on.
 */
export const TherapistSharePopover = (props: TherapistSharePopoverProps): JSX.Element => {
    const { i18n, t: translate } = useTranslation();

    const catalog = useReactQuery<IServiceCatalog>(services.serviceCatalog.getServiceCatalog());
    const practised = useReactQuery<number[]>(
        services.therapists.getCatalogTypes(props.therapistId));

    const disciplines: CatalogTypeChoice[] | undefined = useMemo(() => {
        if (!catalog.data || !practised.data) {
            return undefined;
        }

        const shown = new Set<number>(practised.data);
        props.shares.forEach(share => {
            if (share.catalogTypeId !== null) {
                shown.add(share.catalogTypeId);
            }
        });

        return catalogTypeChoices(catalog.data, i18n.language)
            .filter(choice => shown.has(choice.id));
    }, [catalog.data, practised.data, props.shares, i18n.language]);

    const failed: boolean = catalog.isError || practised.isError;

    return (
        <Popover
            open
            anchorEl={props.anchor}
            onClose={props.onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: popoverPaperStyles } }}
        >
            <Typography variant="subtitle2" sx={popoverTitleStyles}>
                {translate(ResourcesIds.REVENUE_SHARE_TITLE, { name: props.therapistName })}
            </Typography>

            {failed && (
                <Alert severity="error" sx={formAlertStyles}>
                    {translate(ResourcesIds.REVENUE_SHARE_LOAD_ERROR)}
                </Alert>
            )}

            {!failed && !disciplines && (
                <Box sx={loadingStyles}><CircularProgress size={24} /></Box>
            )}

            {!failed && disciplines && (
                <TherapistShareForm {...props} disciplines={disciplines} />
            )}
        </Popover>
    );
};

interface TherapistShareFormProps extends TherapistSharePopoverProps {
    disciplines: CatalogTypeChoice[];
}

/**
 * The form inside the popover. It is an ordinary form: schema, resolver, `useApiFormError` and a
 * blocked-save line, the same as every other form here.
 *
 * <b>It opens on the queued rate where there is one</b>, scope by scope, because that is what a
 * second edit is correcting — opening on the rate in force would quietly offer to undo a change
 * made an hour ago.
 */
const TherapistShareForm = (props: TherapistShareFormProps): JSX.Element => {
    const { i18n, t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const saveShare = useReactMutation(services.therapists.saveRevenueShareMutation());

    const schema = useMemo(() => createTherapistShareSchema(translate), [translate]);

    const seedOf = (catalogTypeId: number | null): number | null =>
        shareEditValue(shareFor(props.shares, props.therapistId, catalogTypeId));

    /**
     * Seeded once, at mount, rather than pushed in by an effect afterwards.
     *
     * <b>`reset` does not validate</b>, so a form seeded that way starts with `isValid` false --
     * which left Save disabled under "fix the fields marked above" the instant the editor opened
     * on a perfectly good rate. The caller mounts this component only while it is open and keys it
     * to the therapist, so every opening is a fresh form and react-hook-form validates the
     * defaults itself.
     */
    const defaultSeed: number | null = seedOf(null);
    const disciplineSeeds: (number | null)[] = props.disciplines.map(choice => seedOf(choice.id));

    const {
        register, handleSubmit, setError, formState: { errors, isValid, isDirty },
    } = useValidatedForm<TherapistShareFormValues>(schema, {
        defaultValues: {
            percent: defaultSeed === null ? '' : formatPercent(defaultSeed),
            disciplines: props.disciplines.map((choice, index) => ({
                catalogTypeId: choice.id,
                percent: disciplineSeeds[index] === null
                    ? ''
                    : formatPercent(disciplineSeeds[index] as number),
            })),
        },
    });

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<TherapistShareFormValues>(
        setError, knownFields, translate, ResourcesIds.REVENUE_SHARE_ERROR);

    /** Something to take away: a rate in force or queued, in any scope. */
    const canRemove: boolean = defaultSeed !== null || disciplineSeeds.some(seed => seed !== null);

    const commit = (values: TherapistShareFormValues | null): void => {
        clear();

        // Removing takes the share away in every scope, so the therapist is back on the whole
        // income rather than on whatever overrides happened to be left.
        const percent: number | null = values === null ? null : Number(values.percent);
        const disciplines = values === null
            ? props.disciplines.map(choice => ({ catalogTypeId: choice.id, percent: null }))
            : values.disciplines.map(row => ({
                catalogTypeId: row.catalogTypeId,
                percent: row.percent.trim() === '' ? null : Number(row.percent),
            }));

        saveShare.mutate({ therapistId: props.therapistId, percent, disciplines }, {
            onSuccess: async (stored: ITherapistRevenueShare[]) => {
                const scheduledFrom: string | null =
                    stored.find(share => share.scheduledFrom)?.scheduledFrom ?? null;

                showSnackbar(
                    translate(
                        percent === null
                            ? ResourcesIds.REVENUE_SHARE_CLEARED_SUCCESS
                            : ResourcesIds.REVENUE_SHARE_SUCCESS,
                        {
                            name: props.therapistName,
                            percent: percent === null ? '' : formatPercent(percent),
                            date: scheduledFrom ? formatRecordDate(scheduledFrom, i18n) : '',
                        }
                    ),
                    'success'
                );

                await props.onSaved();
                props.onClose();
            },
            // Reported rather than swallowed: nothing visible changes on success, and a lost
            // write means somebody is paid the wrong amount.
            onError: report,
        });
    };

    /**
     * The sentence that makes a save that changes nothing on screen legible.
     *
     * Two readings, because a therapist already on a rate is being told something different from
     * one who is not: what is in force *until* the change, versus simply when it starts.
     */
    const inForce: number | null = shareFor(props.shares, props.therapistId)?.percent ?? null;
    const explanation: string = inForce !== null
        ? translate(ResourcesIds.REVENUE_SHARE_EXPLAIN_REPLACING, { current: formatPercent(inForce) })
        : translate(ResourcesIds.REVENUE_SHARE_EXPLAIN_NEW);

    return (
        <>
            {formError && <Alert severity="error" sx={formAlertStyles}>{formError}</Alert>}

            <Box sx={fieldRowStyles}>
                <TextField
                    type="number"
                    size="small"
                    autoFocus
                    sx={fieldStyles}
                    label={translate(ResourcesIds.REVENUE_SHARE_DEFAULT_LABEL)}
                    {...register('percent')}
                    {...fieldProps(errors, 'percent')}
                    slotProps={{
                        ...numericSlotProps(REVENUE_SHARE_PERCENT_BOUNDS),
                        inputLabel: { shrink: true },
                    }}
                />

                <Typography variant="body2" sx={suffixStyles}>%</Typography>
            </Box>

            {props.disciplines.length > 0 && (
                <>
                    <Typography variant="overline" sx={disciplinesTitleStyles}>
                        {translate(ResourcesIds.REVENUE_SHARE_DISCIPLINES_TITLE)}
                    </Typography>

                    {props.disciplines.map((choice, index) => {
                        const message: string | undefined =
                            errors.disciplines?.[index]?.percent?.message;

                        return (
                            <Box key={choice.id} sx={disciplineRowStyles}>
                                <TextField
                                    type="number"
                                    size="small"
                                    sx={fieldStyles}
                                    label={choice.name}
                                    {...register(`disciplines.${index}.percent`)}
                                    error={!!message}
                                    helperText={message}
                                    slotProps={{
                                        ...numericSlotProps(REVENUE_SHARE_PERCENT_BOUNDS),
                                        inputLabel: { shrink: true },
                                    }}
                                />

                                <Typography variant="body2" sx={suffixStyles}>%</Typography>
                            </Box>
                        );
                    })}

                    <Typography variant="caption" sx={disciplinesHintStyles}>
                        {translate(ResourcesIds.REVENUE_SHARE_DISCIPLINES_HINT)}
                    </Typography>
                </>
            )}

            <Typography variant="caption" sx={explanationStyles}>
                {explanation}
            </Typography>

            {/* Never a greyed button with no reason beside it -- the house rule -- but not before
                there is a reason to give. On open, a therapist with no share yet has an empty
                field and an invalid form, and saying "fix the fields marked above" then would
                point at a field nobody has touched and nothing has marked. Once they have typed,
                or a field has complained, the line is earned. */}
            {!isValid && (isDirty || !!errors.percent || !!errors.disciplines) && (
                <Typography variant="caption" sx={blockedSaveStyles}>
                    {translate(ResourcesIds.FORM_BLOCKED_SAVE)}
                </Typography>
            )}

            <Box sx={actionsRowStyles}>
                {/* Away from Cancel and Save: removing a share is a different kind of answer, and
                    it is the one that must never be reachable by a mistyped keystroke.

                    Offered only when there is something to remove, which is the same question
                    `shareEditValue` already answers per scope -- so a therapist on no share is
                    not offered it, and neither is one whose removal is already queued. */}
                {canRemove ? (
                    <Button
                        size="small"
                        color="error"
                        disabled={saveShare.isPending}
                        onClick={() => commit(null)}
                    >
                        {translate(ResourcesIds.REVENUE_SHARE_CLEAR_ACTION)}
                    </Button>
                ) : <span />}

                <Box sx={confirmActionsStyles}>
                    <Button size="small" onClick={props.onClose}>
                        {translate(ResourcesIds.CANCEL_TEXT)}
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        disabled={!isValid || saveShare.isPending}
                        onClick={handleSubmit(values => commit(values))}
                    >
                        {translate(ResourcesIds.SERVICES_SAVE_ACTION)}
                    </Button>
                </Box>
            </Box>
        </>
    );
};
